import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import makeStyles from '@mui/styles/makeStyles';
import {
  AppBar,
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import {
  useApproveManualApplicationStore,
  useAssignManualApplicationStore,
  useAuthUserStore,
  useDeleteFileStore,
  useDownloadFileStore,
  useImpersonatedUserStore,
  useLoggedOnBehalfStore,
  usePartnersStore,
  useRejectManualApplicationFileStore,
  useRejectManualApplicationStore,
  useSearchManualApplicationsStore,
  useSubmitManualApplicationStore,
  useUsersStore,
} from '@lib/store';
import { ApiUtils, AuthApi, JwtToken, ProductApi } from '@shared/api';
import { useRouteHook } from '@shared/hooks';
import { AuthUtils, LocalStorageUtils, PermissionsUtils, RoutePaths } from '@shared/utils';
import { AuthUser, PermissionLevel, PermissionType, RoleType } from '@shared/types';
import { FileList } from '@pages';
import { FileUpload, SnackbarContext } from '@widgets';
import { Button, DelayedLinearProgress, IconButton, Input } from '@shared/widgets';

const useStyles = makeStyles<Theme>((theme) => ({
  postTransactionContainer: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  cardDesign: {
    marginBottom: 20,
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
  },
  appBar: {
    marginBottom: theme.spacing(2),
  },
  title: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  grow: {
    flexGrow: 1,
  },
  toolbarIcon: {
    color: theme.palette.primary.contrastText,
  },
  dropArea: {
    textAlign: 'center',
    padding: theme.spacing(7),
  },
  filePreviewArea: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  button: {
    marginRight: theme.spacing(1),
  },
  formControl: {
    textAlign: 'center',
    minWidth: 200,
    padding: theme.spacing(2),
  },
  fieldComment: {
    minWidth: 300,
  },
}));

const dedupe = (items: string[]) => {
  const index = items.reduce(
    (result, item) => {
      result[item] = true;
      return result;
    },
    {} as Record<string, boolean>,
  );
  return Object.keys(index).sort((a1, a2) => {
    if (a1 < a2) return -1;
    if (a1 > a2) return 1;
    return 0;
  });
};

type Params = {
  manualApplicationId: string;
};

export const ManualApplicationDetailsPage = () => {
  const { manualApplicationId } = useParams<Params>();
  const { navigate } = useRouteHook();

  const { data: authUser } = useAuthUserStore();
  const { setSelectedPartnerId } = usePartnersStore();
  const { setLoggedOnBehalf } = useLoggedOnBehalfStore();
  const { data: impersonatedUser, setImpersonatedUser } = useImpersonatedUserStore();
  const { data: managers, getUsers } = useUsersStore();
  const downloadFileStore = useDownloadFileStore();
  const deleteFileStore = useDeleteFileStore();
  const { loading: loadingApplication, data: manualApplications, searchManualApplications } = useSearchManualApplicationsStore();
  const {
    loading: loadingAssignManualApplication,
    assignManualApplication,
    clear: clearAssignedManualApplication,
  } = useAssignManualApplicationStore();
  const {
    loading: loadingRejectManualApplication,
    rejectManualApplication,
    clear: clearRejectedManualApplication,
  } = useRejectManualApplicationStore();
  const {
    loading: loadingApproveManualApplication,
    approveManualApplication,
    clear: clearApprovedManualApplication,
  } = useApproveManualApplicationStore();
  const {
    loading: loadingSubmitManualApplication,
    submitManualApplication,
    clear: clearSubmittedManualApplication,
  } = useSubmitManualApplicationStore();
  const {
    loading: loadingRejectManualApplicationFile,
    rejectManualApplicationFile,
    clear: clearRejectedManualApplicationFile,
  } = useRejectManualApplicationFileStore();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLogonBehalf, setIsLogonBehalf] = useState<boolean>(false);
  const [rejectingFileId, setRejectingFileId] = useState<string | null>(null);
  const [rejectingFileName, setRejectingFileName] = useState<string | null>(null);
  const [rejectingFileComment, setRejectingFileComment] = useState<string | null>(null);
  const [ippGroup, setIppGroup] = useState<string | undefined>();

  const classes = useStyles();
  const snackbar = useContext(SnackbarContext);

  const manualApplication = useMemo(
    () => manualApplications.find((manualApplication) => manualApplication.manualAppId.toString() === manualApplicationId),
    [manualApplications],
  );
  const circles = useMemo(() => {
    const source = manualApplication ? managers.filter((item) => item.id === manualApplication.userId) : undefined;
    return source
      ? dedupe(
          source.reduce((acc, item) => {
            return acc.concat(item.circles);
          }, [] as string[]),
        )
      : undefined;
  }, [managers, manualApplication]);

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    if (!manualApplicationId) return;
    searchManualApplications({ applicationId: manualApplicationId });
  }, [manualApplicationId]);

  useEffect(() => {
    if (!manualApplication) return;
    setIppGroup(manualApplication.ippGroup);
  }, [manualApplication]);

  const saveIppGroup = async () => {
    if (!ippGroup) return;
    const result = await assignManualApplication({ manualApplicationId: manualApplicationId!, ippGroup });
    if (result.data) {
      searchManualApplications({ applicationId: manualApplicationId });
      snackbar.success('Circle has been successfully updated');
      clearAssignedManualApplication();
    }
  };

  const rejectFile = (fileId: string, fileName?: string) => {
    setRejectingFileComment(null);
    setRejectingFileId(fileId);
    setRejectingFileName(fileName || null);
  };

  const confirmRejectFile = async () => {
    if (!rejectingFileId) return;
    const result = await rejectManualApplicationFile({ fileId: rejectingFileId, comment: rejectingFileComment });
    if (result.data) {
      searchManualApplications({ applicationId: manualApplicationId });
      setRejectingFileId(null);
      setRejectingFileComment(null);
      setRejectingFileName(null);
      snackbar.success('File has been rejected');
      clearRejectedManualApplicationFile();
    }
  };

  const approveManualApplicationHandler = async () => {
    const result = await approveManualApplication({ manualApplicationId: manualApplicationId! });
    if (result.data) {
      searchManualApplications({ applicationId: manualApplicationId });
      snackbar.success('Application has been approved');
      clearApprovedManualApplication();
    }
  };

  const rejectManualApplicationHandler = async () => {
    const result = await rejectManualApplication({ manualApplicationId: manualApplicationId! });
    if (result.data) {
      searchManualApplications({ applicationId: manualApplicationId });
      snackbar.success('Application has been rejected');
      clearRejectedManualApplication();
    }
  };

  const submitManualApplicationHandler = async () => {
    const result = await submitManualApplication(manualApplicationId!);
    if (result.data) {
      searchManualApplications({ applicationId: manualApplicationId });
      snackbar.success('Application has been submitted');
      clearSubmittedManualApplication();
    }
  };

  const logOnBehalf = async (userId: string) => {
    setIsLoading(true);
    setIsLogonBehalf(true);
    const userToken: JwtToken = jwtDecode(authUser!.accessToken);
    if (!userToken) {
      setIsLoading(false);
      setIsLogonBehalf(false);
      snackbar.error('No current user id found');
      return;
    }

    try {
      const result = (await AuthApi.impersonateUser(userId)) as unknown as AuthUser;
      if (!result.accessToken) {
        LocalStorageUtils.removeIppUser();
        throw new Error('Authentication failed.');
      }
      AuthUtils.updateAuthData(result);
      const { decodedToken } = AuthUtils.parseJwtTokenData(result);

      const allPartners = await ProductApi.getPartners();
      const userPartners: number[] = decodedToken?.client_PartnerIds.split(',').map((item) => +item);
      const partners = allPartners.filter((item) => userPartners.indexOf(item.id) >= 0);
      LocalStorageUtils.setPartners(partners);
      LocalStorageUtils.setSelectedPartnerId(allPartners[0].id);
      setSelectedPartnerId(allPartners[0].id);
      setImpersonatedUser(decodedToken?.client_OnBehalfOf || '(Unknown)');
      setLoggedOnBehalf(true);

      navigate(RoutePaths.dashboard.onNavigate());
      snackbar.success(`Logged in as ${impersonatedUser}`);
    } catch (error) {
      setIsLogonBehalf(false);
      snackbar.error(ApiUtils.getError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const deleteFileHandler = async (fileId: string) => {
    const result = await deleteFileStore.deleteFile(fileId);
    if (result.data) {
      snackbar.success('File has been deleted');
      searchManualApplications({ applicationId: manualApplicationId });
    }
  };

  const loading = isLoading || loadingApproveManualApplication || loadingRejectManualApplication;
  // const readOnly =
  //   !(applicationData && applicationData.status === 'Draft') &&
  //   PermissionsUtils.hasPermission(PermissionType.MANUAL_APPLICATIONS, PermissionLevel.WRITE);
  const canUploadFiles = manualApplication?.status !== 'Complete' && PermissionsUtils.hasPermission(PermissionType.IPPFILES, PermissionLevel.WRITE);
  const canDeleteFiles = manualApplication?.status !== 'Complete' && PermissionsUtils.hasPermission(PermissionType.IPPFILES, PermissionLevel.DELETE);
  const canRejectFiles =
    manualApplication?.status === 'Partner Submitted' && PermissionsUtils.hasPermission(PermissionType.IPPFILES, PermissionLevel.APPROVE);
  const canAssignCircle =
    PermissionsUtils.hasRole(RoleType.ADMIN) && PermissionsUtils.hasPermission(PermissionType.MANUAL_APPLICATIONS, PermissionLevel.ASSIGN);
  const hasActiveFiles = useMemo(() => {
    return manualApplication && manualApplication.files && manualApplication.files.filter((item) => item.status !== 'Rejected').length > 0;
  }, [manualApplication?.files]);
  const canSubmitApplication =
    PermissionsUtils.hasPermission(PermissionType.MANUAL_APPLICATIONS, PermissionLevel.WRITE) &&
    manualApplication?.status === 'Draft' &&
    hasActiveFiles;
  const canApproveApplication =
    PermissionsUtils.hasPermission(PermissionType.MANUAL_APPLICATIONS, PermissionLevel.APPROVE) && manualApplication?.status === 'Partner Submitted';
  const canLogOnBehalf =
    PermissionsUtils.hasPermission(PermissionType.GENERAL, PermissionLevel.LOG_ON_BEHALF) && manualApplication?.status === 'Complete';
  const ippGroupChanged = useMemo(() => {
    return manualApplication?.ippGroup !== ippGroup && (!loading || loadingAssignManualApplication) && ippGroup;
  }, [manualApplication, ippGroup, loading, loadingAssignManualApplication]);
  return (
    <>
      <Breadcrumbs aria-label='breadcrumb' sx={{ marginBottom: '12px' }}>
        <Link
          sx={{ textTransform: 'uppercase', fontSize: '12px' }}
          underline='hover'
          color='inherit'
          href={RoutePaths.manualApplications.onNavigate()}
        >
          Manual Applications
        </Link>
        {manualApplication && (
          <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='none'>
            {manualApplication.manRefId}
          </Link>
        )}
      </Breadcrumbs>
      {manualApplication && (
        <AppBar position='static' className={classes.appBar}>
          <Toolbar>
            <Typography className={classes.title} variant='h6' noWrap>
              {manualApplication.manRefId}
            </Typography>
            <div className={classes.grow} />
            <div>
              {canSubmitApplication && (
                <Tooltip title='Submit'>
                  <IconButton size='large' loading={loadingSubmitManualApplication} onClick={submitManualApplicationHandler}>
                    <DoneIcon className={classes.toolbarIcon} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </Toolbar>
        </AppBar>
      )}

      <Grid container alignContent='center'>
        <Grid item xs={12}>
          {canApproveApplication && (
            <>
              <Button
                variant='contained'
                color='primary'
                className={classes.button}
                loading={loadingApproveManualApplication}
                disabled={loading}
                onClick={approveManualApplicationHandler}
              >
                Approve Application
              </Button>
              <Button
                variant='contained'
                color='primary'
                className={classes.button}
                loading={loadingRejectManualApplication}
                disabled={loading}
                onClick={rejectManualApplicationHandler}
              >
                Reject Application
              </Button>
            </>
          )}
          {canLogOnBehalf && (
            <Button
              variant='contained'
              color='primary'
              className={classes.button}
              loading={isLogonBehalf}
              disabled={loading}
              onClick={() => logOnBehalf(manualApplication!.userId!)}
            >
              Log On Behalf
            </Button>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Card className={classes.card}>
            <CardHeader title='Details' />
            <CardContent>
              <Typography component='span' variant='body1'>
                <Grid container>
                  <Grid item xs={12}>
                    {manualApplication ? (
                      <FormControl fullWidth margin='normal' required>
                        <InputLabel>Circle</InputLabel>
                        <Select
                          required
                          name='ippGroup'
                          label='Circle'
                          fullWidth
                          value={ippGroup || manualApplication?.ippGroup || ''}
                          readOnly={!canAssignCircle}
                          onChange={(e) => setIppGroup(e.target.value)}
                        >
                          {circles &&
                            circles.map((item) => (
                              <MenuItem key={item} value={item}>
                                {item}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    ) : (
                      'Loading application details'
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    Status
                  </Grid>
                  <Grid item xs={8}>
                    {!manualApplication ? 'Loading application details' : `${manualApplication.status || '(none)'}`}
                  </Grid>
                </Grid>
                {ippGroupChanged && (
                  <Grid container style={{ justifyContent: 'flex-end' }}>
                    <Grid item xs={12} style={{ justifyContent: 'flex-end', display: 'flex' }}>
                      <Button variant='contained' color='primary' loading={loadingAssignManualApplication} onClick={saveIppGroup}>
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Typography>
            </CardContent>
          </Card>
          {canUploadFiles && <FileUpload onUpload={() => searchManualApplications({ applicationId: manualApplicationId })} />}
        </Grid>
        <Grid item xs={12} md={8}>
          <DelayedLinearProgress loading={loadingApplication || downloadFileStore.loading || deleteFileStore.loading} />
          <Card>
            <CardHeader title='Files' />
            {!manualApplication && !loadingApplication && !downloadFileStore.loading && !deleteFileStore.loading && (
              <Typography align='center'>
                No data to display.
                <br />
                Start by performing a search.
              </Typography>
            )}
            {manualApplication && manualApplication.files && (
              <FileList
                data={manualApplication.files}
                deleteLoading={deleteFileStore.loading}
                showStatus
                onDeleteFile={canDeleteFiles ? deleteFileHandler : undefined}
                onRejectFile={canRejectFiles ? rejectFile : undefined}
              />
            )}
          </Card>
        </Grid>
      </Grid>
      <Dialog disableEscapeKeyDown aria-labelledby='confirmation-dialog-title' open={rejectingFileId !== null}>
        <DialogTitle>Reject File</DialogTitle>
        <DialogContent>
          <p>{rejectingFileName}</p>
          <Input
            className={classes.fieldComment}
            label='Comment'
            multiline
            value={rejectingFileComment || ''}
            onChange={(e) => setRejectingFileComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button loading={loadingRejectManualApplicationFile} onClick={() => confirmRejectFile()}>
            Reject
          </Button>
          <Button autoFocus onClick={() => (setRejectingFileId(null), setRejectingFileName(null))}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
